import { useMemo, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { Comment } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext';
import { useInlineCommentsState } from '@confluence/inline-comments-hooks/entry-points/useInlineComments';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams';
import { useCommentsDataState } from '@confluence/comments-data/entry-points/useCommentsData';
import { fg } from '@confluence/feature-gating';

type UseCommentNavigationProps = {
	pageId: string;
	isEditor?: boolean;
	onNavigationClick: (nextMarkerRef: string) => void;
	commentData: Comment;
};

type useInlineCommentNavigationType = {
	currentCommentIndex: number;
	goToNextInlineComment: () => void;
	goToPrevInlineComment: () => void;
	commentsCount: number;
};

export const useInlineCommentNavigation = ({
	pageId,
	isEditor,
	onNavigationClick,
	commentData,
}: UseCommentNavigationProps): useInlineCommentNavigationType => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { unresolvedInlineComments } = useInlineCommentsState();
	const { orderedActiveAnnotationIdList } = useCommentsDataState();
	const { removeCommentQueryParams } = useInlineCommentQueryParams();
	const { navigateToInlineComment } = useInlineCommentsDispatchContext();

	const orderedActiveAnnotations = useMemo(() => {
		return fg('confluence_frontend_new_comments_scrolling')
			? orderedActiveAnnotationIdList.map((a) => a.threadKey)
			: unresolvedInlineComments;
	}, [orderedActiveAnnotationIdList, unresolvedInlineComments]);

	const getCommentIndex = useCallback(
		(currentMarkerRef: string): number => {
			return orderedActiveAnnotations?.findIndex((markerRef) => markerRef === currentMarkerRef);
		},
		[orderedActiveAnnotations],
	);

	const currentInlineMarkerRef = commentData?.location?.inlineMarkerRef ?? '';
	const currentCommentIndex = getCommentIndex(currentInlineMarkerRef);

	const goToNextInlineComment = useCallback(
		(triggeredByKeyboardShortcut = false) => {
			navigateToInlineComment({
				action: 'next',
				triggeredByKeyboardShortcut,
				currentCommentIndex,
				getCommentIndex,
				onNavigationClick,
				isEditor,
				removeCommentQueryParams,
				unresolvedInlineComments: orderedActiveAnnotations,
				createAnalyticsEvent,
				contentId: pageId,
			});
		},
		[
			getCommentIndex,
			navigateToInlineComment,
			createAnalyticsEvent,
			currentCommentIndex,
			onNavigationClick,
			pageId,
			removeCommentQueryParams,
			orderedActiveAnnotations,
			isEditor,
		],
	);

	const goToPrevInlineComment = useCallback(
		(triggeredByKeyboardShortcut = false) => {
			navigateToInlineComment({
				action: 'previous',
				triggeredByKeyboardShortcut,
				currentCommentIndex,
				getCommentIndex,
				onNavigationClick,
				isEditor,
				removeCommentQueryParams,
				unresolvedInlineComments: orderedActiveAnnotations,
				createAnalyticsEvent,
				contentId: pageId,
			});
		},
		[
			getCommentIndex,
			navigateToInlineComment,
			createAnalyticsEvent,
			currentCommentIndex,
			onNavigationClick,
			pageId,
			removeCommentQueryParams,
			orderedActiveAnnotations,
			isEditor,
		],
	);

	return useMemo(
		() => ({
			currentCommentIndex,
			goToNextInlineComment,
			goToPrevInlineComment,
			commentsCount: orderedActiveAnnotations.length,
		}),
		[
			goToNextInlineComment,
			goToPrevInlineComment,
			orderedActiveAnnotations.length,
			currentCommentIndex,
		],
	);
};
